import { ParentRelationshipType, WorkTaskDTO2, WorkTaskRelativeDTO, WorkTaskStatus } from '../../api/api';
import { getDistanceInDays } from '../../utils/dateHandling';

/**
 * Where a related work task is drawn relative to the selected one.
 *
 * - `preparation` — happens before the selected task (left column)
 * - `visit` — happens together with the selected task, same work order/booking (middle column)
 * - `followUp` — happens after the selected task (right column)
 */
export type RelationSide = 'preparation' | 'visit' | 'followUp';

export interface RelationTask {
  id: number;
  title: string;
  status?: WorkTaskStatus;
  assignedToName?: string;
  assetId?: string;
  /** Only used to group the diagram into visits - the node itself shows the asset id. */
  workOrderNumber?: string;
  fromDate?: string;
  toDate?: string;
  side: RelationSide;
  isSelected: boolean;
  /** How this task relates to the task on the other end of its connector - drawn in the connector badge. */
  relationshipType?: ParentRelationshipType;
  /** Small text drawn on the connector line, e.g. "10-15 dage før". */
  dayLabel?: string;
  /**
   * True only for the selected task's own parent (`selectedTask.parent`). Every other related task
   * is a child of the selected task. The diagram uses this to know which end of a connector is the
   * actual parent in the relationship, since that is not always the selected task itself.
   */
  isParentOfSelected?: boolean;
}

const referenceDate = (task: WorkTaskDTO2 | WorkTaskRelativeDTO) => task.plannedArrival ?? task.earliestStartDate;

/** A single day count needs no "før"/"efter" suffix - only a range does, to show which end it counts from. */
const formatDayCount = (days: number) => (days === 1 ? '1 dag' : `${days} dage`);

const formatDayRange = (min: number | undefined, max: number | undefined, suffix: 'før' | 'efter') => {
  if (min == null && max == null) return undefined;
  if (min != null && max != null && min !== max) return `${min}-${max} dage ${suffix}`;
  return formatDayCount((max ?? min) as number);
};

/** Fallback for older relations that carry dates but no day window. */
const daysBetween = (mainTask: WorkTaskDTO2 | WorkTaskRelativeDTO, followUpTask: WorkTaskDTO2 | WorkTaskRelativeDTO) => {
  // The same end of the main task that a new follow up task is scheduled from, see getRelationTaskSeed.
  const from = mainTask.deadline ?? referenceDate(mainTask);
  const to = referenceDate(followUpTask);
  if (!from || !to) return undefined;

  const days = getDistanceInDays(new Date(from), new Date(to));
  if (!Number.isFinite(days) || days <= 0) return undefined;

  return formatDayCount(days);
};

/**
 * A child points at its parent through its own relationship type, so the side a related task
 * belongs on depends on whether it is a child of the selected task or its parent.
 */
const childSide = (relationshipType?: ParentRelationshipType): RelationSide => {
  switch (relationshipType) {
    case ParentRelationshipType.PreparationForParent:
      return 'preparation';
    case ParentRelationshipType.ExecutedWithParent:
    case ParentRelationshipType.FollowUpSameWorkOrderAsParent:
      return 'visit';
    default:
      return 'followUp';
  }
};

const parentSide = (relationshipType?: ParentRelationshipType): RelationSide => {
  switch (relationshipType) {
    case ParentRelationshipType.PreparationForParent:
      // The selected task prepares for its parent, so the parent comes after it.
      return 'followUp';
    case ParentRelationshipType.ExecutedWithParent:
    case ParentRelationshipType.FollowUpSameWorkOrderAsParent:
      return 'visit';
    default:
      return 'preparation';
  }
};

const toRelationTask = (
  relative: WorkTaskRelativeDTO,
  side: RelationSide,
  dayLabel?: string,
  isParentOfSelected = false,
  relationshipType?: ParentRelationshipType
): RelationTask | undefined => {
  if (!relative.workTaskId) return undefined;

  return {
    id: relative.workTaskId,
    title: relative.taskTypeName ?? `Opgave ${relative.workTaskId}`,
    status: relative.status,
    assignedToName: relative.assignedToName ?? undefined,
    assetId: relative.assetId ?? undefined,
    workOrderNumber: relative.dynamicsWorkOrderNumber ?? undefined,
    fromDate: referenceDate(relative),
    toDate: relative.deadline ?? undefined,
    // The relationship type alone decides the column - a preparation or follow up task can share the
    // selected task's work order number and still be on another visit.
    side,
    isSelected: false,
    relationshipType,
    dayLabel,
    isParentOfSelected
  };
};

const selectedRelationTask = (task: WorkTaskDTO2, id: number): RelationTask => ({
  id,
  title: task.taskType?.name ?? `Opgave ${id}`,
  status: task.status,
  assignedToName: task.assignedToName ?? undefined,
  assetId: task.assetId ?? undefined,
  workOrderNumber: task.dynamicsWorkOrderNumber ?? undefined,
  fromDate: referenceDate(task),
  toDate: task.deadline ?? undefined,
  side: 'visit',
  isSelected: true
});

/**
 * Turns the selected work task and its direct relatives into the nodes of the diagram. Only one
 * level in each direction is included, which is what the diagram shows.
 */
export const buildRelationTasks = (selectedTask?: WorkTaskDTO2): RelationTask[] => {
  const selectedId = parseInt(selectedTask?.id ?? '');
  if (!selectedTask || !selectedId) return [];

  const selected = selectedRelationTask(selectedTask, selectedId);

  const children = (selectedTask.children ?? []).reduce<RelationTask[]>((tasks, child) => {
    if (child.workTaskId === selectedId) return tasks;

    const side = childSide(child.parentRelationshipType);
    const dayLabel =
      side === 'preparation'
        ? formatDayRange(child.minDaysToParent, child.maxDaysToParent, 'før')
        : formatDayRange(child.minDaysToParent, child.maxDaysToParent, 'efter') ?? daysBetween(selectedTask, child);

    const node = toRelationTask(child, side, dayLabel, false, child.parentRelationshipType);
    return node ? [...tasks, node] : tasks;
  }, []);

  const parentSideOfSelected = parentSide(selectedTask.parentRelationshipType);
  const parentDayLabel =
    parentSideOfSelected === 'followUp'
      ? formatDayRange(selectedTask.minTimeBeforeMainTaskDays, selectedTask.maxTimeBeforeMainTaskDays, 'før')
      : formatDayRange(selectedTask.minTimeBeforeMainTaskDays, selectedTask.maxTimeBeforeMainTaskDays, 'efter') ??
        (selectedTask.parent ? daysBetween(selectedTask.parent, selectedTask) : undefined);
  const parent = selectedTask.parent
    ? toRelationTask(selectedTask.parent, parentSideOfSelected, parentDayLabel, true, selectedTask.parentRelationshipType)
    : undefined;

  return parent ? [selected, parent, ...children] : [selected, ...children];
};
